import React, { useEffect, useState } from 'react';
import { assetsAPI, trainingAPI, performanceAPI } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Loader2, Laptop, GraduationCap, Target, Star } from 'lucide-react';

const STATUS_COLORS = { available: 'default', assigned: 'secondary', under_repair: 'destructive', lost: 'destructive' };
const TRAIN_COLORS = { scheduled: 'outline', in_progress: 'secondary', completed: 'default', cancelled: 'destructive' };

const MyDevelopment = () => {
  const [loading, setLoading] = useState(true);
  const [assets, setAssets] = useState([]);
  const [training, setTraining] = useState([]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [a, t, r] = await Promise.all([
          assetsAPI.myAssets(),
          trainingAPI.mine(),
          performanceAPI.mine(),
        ]);
        setAssets(a.data);
        setTraining(t.data);
        setReviews(r.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="my-development-page">
      <div>
        <h1 className="text-2xl font-bold">My Development</h1>
        <p className="text-muted-foreground">Your assigned assets, trainings and performance reviews</p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Card><CardContent className="p-4 flex items-center gap-3"><Laptop className="text-blue-600" /><div><p className="text-xs text-muted-foreground">My Assets</p><p className="text-2xl font-bold">{assets.length}</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><GraduationCap className="text-emerald-600" /><div><p className="text-xs text-muted-foreground">My Trainings</p><p className="text-2xl font-bold">{training.length}</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><Target className="text-amber-600" /><div><p className="text-xs text-muted-foreground">My Reviews</p><p className="text-2xl font-bold">{reviews.length}</p></div></CardContent></Card>
      </div>

      <Tabs defaultValue="assets">
        <TabsList>
          <TabsTrigger value="assets" data-testid="my-assets-tab">Assets ({assets.length})</TabsTrigger>
          <TabsTrigger value="training" data-testid="my-training-tab">Training ({training.length})</TabsTrigger>
          <TabsTrigger value="performance" data-testid="my-performance-tab">Performance ({reviews.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="assets">
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Tag</TableHead><TableHead>Name</TableHead><TableHead>Category</TableHead>
                <TableHead>Serial</TableHead><TableHead>Status</TableHead><TableHead>Assigned Since</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {assets.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">No assets assigned</TableCell></TableRow>}
                {assets.map(a => (
                  <TableRow key={a.id}>
                    <TableCell className="font-mono">{a.asset_tag}</TableCell>
                    <TableCell>{a.name}</TableCell>
                    <TableCell><Badge variant="outline">{a.category}</Badge></TableCell>
                    <TableCell>{a.serial_number || '—'}</TableCell>
                    <TableCell><Badge variant={STATUS_COLORS[a.status]}>{(a.status || '').replace(/_/g, ' ')}</Badge></TableCell>
                    <TableCell className="text-xs">{a.assigned_at ? new Date(a.assigned_at).toLocaleDateString() : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="training">
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Title</TableHead><TableHead>Category</TableHead><TableHead>Provider</TableHead>
                <TableHead>Dates</TableHead><TableHead>Status</TableHead><TableHead>Score</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {training.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">No trainings assigned</TableCell></TableRow>}
                {training.map(t => (
                  <TableRow key={t.id}>
                    <TableCell className="font-medium">{t.title}</TableCell>
                    <TableCell><Badge variant="outline">{t.category || '—'}</Badge></TableCell>
                    <TableCell>{t.provider || '—'}</TableCell>
                    <TableCell className="text-xs">{t.start_date || '—'} → {t.end_date || '—'}</TableCell>
                    <TableCell><Badge variant={TRAIN_COLORS[t.status]}>{(t.status || '').replace(/_/g, ' ')}</Badge></TableCell>
                    <TableCell>{t.score ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="performance">
          <div className="space-y-3">
            {reviews.length === 0 && <Card><CardContent className="p-8 text-center text-muted-foreground">No reviews yet</CardContent></Card>}
            {reviews.map(r => (
              <Card key={r.id}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold">{r.review_period}</p>
                      <p className="text-xs text-muted-foreground">Reviewed by {r.reviewer_email}</p>
                    </div>
                    <Badge variant="default" className="text-base px-3 py-1 flex items-center gap-1">{r.overall_rating ?? '—'}/5 <Star size={14} className="fill-current" /></Badge>
                  </div>
                  {r.goals?.length > 0 && (
                    <div className="text-sm">
                      <p className="font-medium mb-1">Goals:</p>
                      <ul className="list-disc pl-5 space-y-0.5">
                        {r.goals.map((g, i) => <li key={i}><span className="font-medium">{g.name}:</span> target {g.target}, achieved {g.achieved}</li>)}
                      </ul>
                    </div>
                  )}
                  {r.strengths && <div className="text-sm"><span className="font-medium">Strengths:</span> {r.strengths}</div>}
                  {r.improvements && <div className="text-sm"><span className="font-medium">Improvements:</span> {r.improvements}</div>}
                  {r.manager_comments && <div className="text-sm bg-muted/50 p-2 rounded"><span className="font-medium">Manager:</span> {r.manager_comments}</div>}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MyDevelopment;
